import { cn } from '@/lib/utils';
import * as React from 'react';

export type FosVariant =
  | 'default'
  | 'primary'
  | 'ghost'
  | 'danger'
  | 'accent'
  | 'success'
  | 'outline'
  | 'secondary'
  | 'destructive'
  | 'link';
export type FosSize = 'xs' | 'sm' | 'md' | 'icon' | 'icon-xs';
export type FosBadgeVariant =
  | 'default'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'secondary'
  | 'destructive'
  | 'outline';

const buttonSizes: Record<FosSize, string> = {
  xs: 'h-6 px-2 text-[11px] gap-1',
  sm: 'h-[30px] px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-[13px] gap-2',
  icon: 'h-[30px] w-[30px] p-0 text-xs',
  'icon-xs': 'h-6 w-6 p-0 text-[11px]',
};

const buttonVariants: Record<FosVariant, string> = {
  default: 'bg-fos-bg-elevated text-fos-text-primary border-fos-border hover:bg-fos-bg-hover',
  primary: 'bg-fos-accent text-fos-text-inverse border-fos-accent hover:bg-fos-accent-hover',
  ghost: 'bg-transparent text-fos-text-secondary border-transparent hover:bg-fos-bg-elevated',
  danger:
    'bg-fos-red-bg text-fos-red border-[rgba(248,113,113,0.2)] hover:bg-[rgba(248,113,113,0.15)]',
  accent: 'bg-fos-accent-bg text-fos-accent border-fos-accent-border hover:bg-fos-accent-bg-strong',
  success:
    'bg-fos-green-bg text-fos-green border-[rgba(74,222,128,0.2)] hover:bg-[rgba(74,222,128,0.15)]',
  outline: 'border border-fos-border bg-transparent text-fos-text-primary hover:bg-fos-bg-elevated',
  secondary: 'bg-fos-bg-elevated text-fos-text-primary border-fos-border hover:bg-fos-bg-card',
  destructive: 'bg-fos-red text-white border-fos-red hover:bg-fos-red/90',
  link: 'text-fos-accent underline-offset-4 hover:underline border-transparent bg-transparent',
};

export interface FosButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: FosVariant;
  size?: FosSize;
}

export const FosButton = React.forwardRef<HTMLButtonElement, FosButtonProps>(
  ({ variant = 'default', size = 'sm', className, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center font-medium leading-none whitespace-nowrap',
        'rounded-fos border transition-colors duration-fos',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        buttonSizes[size],
        buttonVariants[variant],
        className,
      )}
      {...props}
    />
  ),
);
FosButton.displayName = 'FosButton';

const badgeVariants: Record<FosBadgeVariant, string> = {
  default: 'bg-fos-bg-elevated text-fos-text-secondary border-fos-border',
  accent: 'bg-fos-accent-bg text-fos-accent border-fos-accent-border',
  success: 'bg-fos-green-bg text-fos-green border-[rgba(74,222,128,0.2)]',
  warning: 'bg-fos-amber-bg text-fos-amber border-[rgba(251,191,36,0.2)]',
  danger: 'bg-fos-red-bg text-fos-red border-[rgba(248,113,113,0.2)]',
  info: 'bg-fos-blue-bg text-fos-blue border-[rgba(96,165,250,0.2)]',
  secondary: 'bg-fos-bg-elevated text-fos-text-secondary border-fos-border',
  destructive: 'bg-fos-red-bg text-fos-red border-[rgba(248,113,113,0.2)]',
  outline: 'bg-transparent text-fos-text-secondary border-fos-border',
};

const dotColor: Record<FosBadgeVariant, string> = {
  default: 'bg-fos-text-secondary',
  accent: 'bg-fos-accent',
  success: 'bg-fos-green',
  warning: 'bg-fos-amber',
  danger: 'bg-fos-red',
  info: 'bg-fos-blue',
  secondary: 'bg-fos-text-secondary',
  destructive: 'bg-fos-red',
  outline: 'bg-fos-text-secondary',
};

export interface FosBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: FosBadgeVariant;
  dot?: boolean;
}

export function FosBadge({
  variant = 'default',
  dot,
  className,
  children,
  ...rest
}: FosBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border',
        'px-2 py-0.5 text-[11px] font-medium leading-none',
        badgeVariants[variant],
        className,
      )}
      {...rest}
    >
      {dot && <span className={cn('h-[5px] w-[5px] rounded-full', dotColor[variant])} />}
      {children}
    </span>
  );
}

export interface FosCardProps extends React.HTMLAttributes<HTMLDivElement> {
  hoverable?: boolean;
}

export const FosCard = React.forwardRef<HTMLDivElement, FosCardProps>(
  ({ hoverable, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        'bg-fos-bg-card border border-fos-border rounded-fos-md overflow-hidden',
        'transition-colors duration-fos',
        hoverable && 'hover:border-fos-border-strong cursor-pointer',
        className,
      )}
      {...rest}
    />
  ),
);
FosCard.displayName = 'FosCard';

export interface FosKpiCardProps {
  label: string;
  value: React.ReactNode;
  change?: React.ReactNode;
  /** true = positive (green), false = negative (red), undefined = neutral */
  positive?: boolean;
  className?: string;
}

export function FosKpiCard({ label, value, change, positive, className }: FosKpiCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1 px-4 py-3.5',
        'bg-fos-bg-card border border-fos-border rounded-fos-md',
        'flex-1 min-w-[130px]',
        className,
      )}
    >
      <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-fos-text-muted">
        {label}
      </span>
      <span className="fos-kpi-val font-mono text-[22px] font-bold leading-none text-fos-text-primary">
        {value}
      </span>
      {change != null && (
        <span
          className={cn(
            'flex items-center gap-1 font-mono text-[11px] font-medium',
            positive === true && 'text-fos-green',
            positive === false && 'text-fos-red',
            positive === undefined && 'text-fos-text-muted',
          )}
        >
          {change}
        </span>
      )}
    </div>
  );
}

export interface FosSearchInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange'
> {
  value: string;
  onValueChange: (v: string) => void;
  containerClassName?: string;
  /** Optional leading icon node (e.g. <Search size={14} />). Defaults to a dim search glyph. */
  leadingIcon?: React.ReactNode;
}

export const FosSearchInput = React.forwardRef<HTMLInputElement, FosSearchInputProps>(
  ({ value, onValueChange, placeholder, containerClassName, leadingIcon, ...rest }, ref) => (
    <div
      className={cn(
        'flex items-center gap-2 flex-1 h-8 px-2.5',
        'bg-fos-bg-surface border border-fos-border rounded-fos',
        'transition-colors duration-fos focus-within:border-fos-accent-border',
        containerClassName,
      )}
    >
      <span className="flex-shrink-0 text-fos-text-muted">
        {leadingIcon ?? (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        )}
      </span>
      <input
        ref={ref}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 border-none bg-transparent text-[13px] text-fos-text-primary outline-none placeholder:text-fos-text-muted"
        {...rest}
      />
      {value && (
        <button
          type="button"
          aria-label="Clear"
          onClick={() => onValueChange('')}
          className="text-fos-text-muted transition-colors duration-fos hover:text-fos-text-secondary"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  ),
);
FosSearchInput.displayName = 'FosSearchInput';

export interface FosCheckboxProps {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  className?: string;
}

export function FosCheckbox({ checked, onCheckedChange, className }: FosCheckboxProps) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={(e) => {
        e.stopPropagation();
        onCheckedChange(!checked);
      }}
      className={cn(
        'h-4 w-4 rounded-[3px] flex items-center justify-center border-[1.5px]',
        'transition-colors duration-fos flex-shrink-0',
        checked ? 'bg-fos-accent border-fos-accent' : 'bg-transparent border-fos-border-strong',
        className,
      )}
    >
      {checked && (
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-fos-text-inverse"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </button>
  );
}

export interface FosMiniStatProps {
  label: string;
  value: React.ReactNode;
  /** Tailwind class to color the value (e.g. `text-fos-green`). Falls back
   *  to `text-fos-text-primary` for neutral. */
  valueClassName?: string;
  className?: string;
}

/** Compact stat tile from the design's `MiniStat` row — value + label sit
 *  on a single baseline, designed for top-of-page status strips. Cousin of
 *  `FosKpiCard` which stacks value above label and supports a change row. */
export function FosMiniStat({ label, value, valueClassName, className }: FosMiniStatProps) {
  return (
    <div
      className={cn(
        'flex flex-1 items-baseline gap-2 rounded-fos-md border border-fos-border bg-fos-bg-card px-3.5 py-2.5',
        className,
      )}
    >
      <span
        className={cn('font-mono text-[20px] font-bold', valueClassName ?? 'text-fos-text-primary')}
      >
        {value}
      </span>
      <span className="text-[11px] text-fos-text-muted">{label}</span>
    </div>
  );
}

/** Per-platform pill colors. Read from CSS custom properties defined
 *  in `src/index.css` so the dark-mode `.dark` block can override
 *  them — without that, the cream-tuned light-theme literals were
 *  ~2-3:1 contrast on dark backdrops. The design still treats each
 *  marketplace's brand color as a fixed identifier (i.e. accent-
 *  swapping in the Tweaks panel doesn't touch these); only the
 *  light/dark theme toggle does. */
const PLATFORM_PALETTE: Record<string, { bg: string; color: string; border: string }> = {
  eBay: {
    bg: 'var(--fos-platform-ebay-bg)',
    color: 'var(--fos-platform-ebay-color)',
    border: 'var(--fos-platform-ebay-border)',
  },
  Etsy: {
    bg: 'var(--fos-platform-etsy-bg)',
    color: 'var(--fos-platform-etsy-color)',
    border: 'var(--fos-platform-etsy-border)',
  },
  Mercari: {
    bg: 'var(--fos-platform-mercari-bg)',
    color: 'var(--fos-platform-mercari-color)',
    border: 'var(--fos-platform-mercari-border)',
  },
  Poshmark: {
    bg: 'var(--fos-platform-poshmark-bg)',
    color: 'var(--fos-platform-poshmark-color)',
    border: 'var(--fos-platform-poshmark-border)',
  },
  Facebook: {
    bg: 'var(--fos-platform-facebook-bg)',
    color: 'var(--fos-platform-facebook-color)',
    border: 'var(--fos-platform-facebook-border)',
  },
};

export interface FosPlatformBadgeProps {
  /** Marketplace name. Anything not in the registered palette renders as
   *  a neutral "Draft" pill (matches design's fallback). */
  name: string;
  className?: string;
}

/** Compact pill for marketplace identity (eBay, Etsy, Mercari, Poshmark,
 *  Facebook). Uses each platform's brand color rather than the user's
 *  current accent so listings remain visually scannable across pages. */
export function FosPlatformBadge({ name, className }: FosPlatformBadgeProps) {
  const palette = PLATFORM_PALETTE[name];
  if (!palette) {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full border border-fos-border bg-fos-bg-elevated px-1.5 py-0.5 text-[10px] font-medium text-fos-text-muted',
          className,
        )}
      >
        Draft
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
        className,
      )}
      style={{
        background: palette.bg,
        color: palette.color,
        borderColor: palette.border,
      }}
    >
      {name}
    </span>
  );
}

/** Inline price editor matching the design's `PriceCell` pattern. Renders
 *  as a static `$N` until clicked; clicking swaps in an input that commits
 *  on Enter or blur, reverts on Escape. Pencil icon hint shows on row
 *  hover via the `rowHover` prop (passed in from the parent row). */
export interface FosInlinePriceProps {
  price: number;
  onChange: (next: number) => void;
  /** Whether the parent row is hovered — controls the edit-glyph fade.
   *  Optional; if omitted the glyph stays hidden until focus. */
  rowHover?: boolean;
  className?: string;
  /** Display prefix; defaults to `$`. */
  currency?: string;
}

export function FosInlinePrice({
  price,
  onChange,
  rowHover,
  className,
  currency = '$',
}: FosInlinePriceProps) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(String(price));
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  React.useEffect(() => {
    setVal(String(price));
  }, [price]);

  const commit = () => {
    const n = Number(val);
    if (Number.isFinite(n) && n > 0 && n !== price) {
      onChange(n);
    } else {
      setVal(String(price));
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <div className={cn('flex items-center gap-0', className)}>
        <span className="mr-0.5 text-[12px] text-fos-text-muted">{currency}</span>
        <input
          ref={inputRef}
          type="number"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setVal(String(price));
              setEditing(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'w-[60px] rounded-fos-sm border border-fos-accent-border bg-fos-bg-surface px-1.5 py-0.5',
            'font-mono text-[12px] font-bold text-fos-accent outline-none',
          )}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      title="Click to edit price"
      className={cn('inline-flex cursor-text items-center gap-1 font-mono text-[12px]', className)}
    >
      <span className="font-semibold">
        {currency}
        {price}
      </span>
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn(
          'text-fos-text-muted transition-opacity duration-fos',
          rowHover ? 'opacity-60' : 'opacity-0',
        )}
      >
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    </button>
  );
}

/** Spinner matching the design's .spinner / .spinner-lg classes. */
export function FosSpinner({ size = 14, className }: { size?: number; className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      style={{ width: size, height: size, borderWidth: size >= 24 ? 3 : 2 }}
      className={cn(
        'inline-block rounded-full border-fos-border border-t-fos-accent',
        'animate-fos-spin',
        className,
      )}
    />
  );
}
